import { z } from 'zod';
import { zAlertKind, zAlertStatus, zPartyType, zVehicleStatus } from './domain.js';

/**
 * Transport shape. Lists always answer `{ data, meta }`; failures always answer
 * `{ error: { code, message, details? } }` with a matching HTTP status.
 */
export interface PageMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface Paginated<T> {
  data: T[];
  meta: PageMeta;
}

export interface ApiError {
  error: { code: string; message: string; details?: unknown };
}

export const ERROR_CODES = {
  BAD_REQUEST: 'BAD_REQUEST',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL: 'INTERNAL',
  SYNC_UNCONFIGURED: 'SYNC_UNCONFIGURED',
} as const;

export const zPagination = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(50),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type Pagination = z.infer<typeof zPagination>;

/** One filter vocabulary, used by stock lists, exports and reports alike. */
export const zVehicleFilters = z.object({
  q: z.string().trim().min(1).optional(),
  status: z.array(zVehicleStatus).or(zVehicleStatus.transform((s) => [s])).optional(),
  partyId: z.string().optional(),
  /**
   * Every vehicle that ever reached this party, wherever it is now — an
   * Autozone dealer's page lists what it bought, sold on or not (1 Oct 2026).
   */
  boughtBy: z.string().optional(),
  /**
   * Every vehicle this mitr or outlet sold to a customer, wherever it is now —
   * the same SALE hops its Sold figure counts (owner, 28 Oct 2026: "i can see
   * which outlet did what number of sales but no details").
   */
  soldBy: z.string().optional(),
  partyType: zPartyType.optional(),
  asmId: z.string().optional(),
  model: z.array(z.string()).or(z.string().transform((s) => [s])).optional(),
  variant: z.array(z.string()).or(z.string().transform((s) => [s])).optional(),
  color: z.array(z.string()).or(z.string().transform((s) => [s])).optional(),
  invoiceNo: z.string().optional(),
  /**
   * RTO code — `UP32` — or `NONE` for vehicles carrying no registration.
   *
   * One filter shared by the stock list, the ageing workspace, the dashboard and
   * the analytics, because they all read the same vehicle query: adding it here
   * puts region on every one of them at once.
   */
  region: z.array(z.string()).or(z.string().transform((s) => [s])).optional(),
  /**
   * Recorded area — `Party.city`, the owner's own vocabulary from the mitra
   * directory. A vehicle matches when its current holder sits in the area, or,
   * once sold, when the mitr that sold it does — so "Chinhat" answers both
   * "what is standing in Chinhat" and "what did Chinhat sell".
   */
  area: z.array(z.string()).or(z.string().transform((s) => [s])).optional(),
  /**
   * The battery and the motor (owner, 21 Oct 2026: "filter as well for battery
   * size type, motor power type like 1250w motor and 1650 motor") — read off
   * the record's `battery_ah` and `motor` text by the marks in vehicle-spec.ts.
   */
  battery: z.array(z.enum(['LITHIUM', 'LEAD', 'NONE'])).or(z.enum(['LITHIUM', 'LEAD', 'NONE']).transform((s) => [s])).optional(),
  batteryAh: z.array(z.string().regex(/^\d{2,3}$/)).or(z.string().regex(/^\d{2,3}$/).transform((s) => [s])).optional(),
  motor: z.array(z.string().regex(/^\d{3,4}$/)).or(z.string().regex(/^\d{3,4}$/).transform((s) => [s])).optional(),
  billingMonth: z.string().regex(/^\d{4}-\d{2}$/).optional(),
  billingFrom: z.string().optional(),
  billingTo: z.string().optional(),
  ageingBucket: z
    .enum(['D0_15', 'D16_30', 'D31_60', 'D61_90', 'D91_180', 'D181_365', 'D365_PLUS'])
    .optional(),
  minDays: z.coerce.number().int().min(0).optional(),
  maxDays: z.coerce.number().int().min(0).optional(),
});
export type VehicleFilters = z.infer<typeof zVehicleFilters>;

export const zVehicleQuery = zVehicleFilters.merge(zPagination);

export const zAlertQuery = z
  .object({
    kind: zAlertKind.optional(),
    status: zAlertStatus.optional(),
    severity: z.enum(['INFO', 'WARNING', 'CRITICAL']).optional(),
  })
  .merge(zPagination);

export interface VehicleListItem {
  id: string;
  chassisNo: string;
  vin: string | null;
  model: string;
  variant: string | null;
  color: string | null;
  invoiceNo: string | null;
  billingDate: string | null;
  status: string;
  custodyParty: { id: string; name: string; type: string } | null;
  asm: { id: string; name: string } | null;
  daysInStock: number | null;
  ageingBucket: string | null;
  invoiceValue: string | null;
  /**
   * The sale, for a list that is showing sold vehicles (owner, 16 Sep 2026:
   * "sold vehicles section on side bar which will show the sold vehicles along
   * with chasis number — sales section only shows performance no details").
   * Null on anything still standing.
   */
  soldAt: string | null;
  saleAmount: string | null;
  buyerName: string | null;
  /**
   * What the billing desk did with a sold vehicle, so a mitr sees the bill and
   * the insurance policy land on their own sold list (owner, 16 Sep 2026: "it
   * will also refelect in mitra portel"). All null on anything still standing.
   */
  billNo: string | null;
  billDocId: string | null;
  insuranceDocId: string | null;
  /** The fixed heads charged on top of the list price, as they stood on the day. */
  rtoAmount: string | null;
  insuranceAmount: string | null;
  ownInsurance: boolean;
}

export interface DashboardSummary {
  totalPurchased: number;
  brightStock: number;
  dealerStock: number;
  subDealerStock: number;
  outletStock: number;
  directCustomer: number;
  sold: number;
  /** Came back to Bright Autozone. Out of Bright on Wheels' book. */
  returned: number;
  pendingAllocation: number;
  inventoryValue: string | null;
  valuedVehicles: number;
  /**
   * How many parties there are in each channel, and how many of them are
   * actually holding something today.
   *
   * "127 with mitrs" answers half a question: 127 across five mitrs and 127
   * across ninety-five are different businesses. The counts sit beside the
   * vehicle totals so neither has to be inferred from the other.
   */
  network: {
    outlets: NetworkCount;
    mitrs: NetworkCount;
    dealers: NetworkCount;
  };
  asOf: string;
}

export interface NetworkCount {
  /**
   * Parties of this kind the sheet still names — or that are holding stock,
   * which keeps them live whatever the sheet has been renamed to since.
   */
  total: number;
  /** Of those, the ones holding at least one vehicle right now. */
  holding: number;
  /**
   * Names the newest sync no longer met: spellings the owner has corrected, and
   * channels he has re-filed. Kept in the database because their movements are
   * the custody history of vehicles they really handled, and kept out of `total`
   * because 33 dead outlet names in a count of 52 is not a count of anything.
   */
  retired: number;
}

export interface PendingDepositItem {
  id: string;
  chassisNo: string;
  model: string;
  variant: string | null;
  color: string | null;
  customerName: string | null;
  saleType: string | null;
  saleAmount: string | null;
  fileChargeAmount: string | null;
  financeAmount: string | null;
  financier: string | null;
  pendingAmount: string | null;
  depositDueAt: string | null;
  daysRemaining: number | null;
  isOverdue: boolean;
  depositStatus: string;
  depositReceiptNo: string | null;
  depositDate: string | null;
  depositBank: string | null;
  depositAmount: string | null;
  depositSubmittedAt: string | null;
  depositNotes: string | null;
  outlet: { id: string; name: string; type: string } | null;
  soldAt: string | null;
}

export interface PendingDepositsResponse {
  items: PendingDepositItem[];
  count: number;
  totalPendingAmount: number;
}
